import { create, UseBoundStore, StoreApi } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

type ScalarStore<VALUE> = UseBoundStore<
  StoreApi<{
    value: VALUE;
    set: (value: VALUE) => void;
  }>
> & { getValue: () => VALUE; setValue: (value: VALUE) => void };

type StorageOptions = {
  key: string;
  storage: 'local' | 'session';
};

// Little util to make the API even lighter, like the one of `useState`, except that it optionally accepts storage options
export const createScalarStore = <VALUE>(
  initialValue: VALUE,
  storageOptions?: StorageOptions
): UseBoundStore<
  StoreApi<{
    value: VALUE;
    set: (value: VALUE) => void;
  }>
> & { getValue: () => VALUE; setValue: (value: VALUE) => void } => {
  const storage =
    storageOptions?.storage === 'session' ? sessionStorage : localStorage;

  const hook = (
    storageOptions
      ? create(
          persist(
            () => ({
              value: initialValue,
              set: (value: VALUE): void => hook.setState({ value }),
            }),
            {
              name: storageOptions.key,
              storage: createJSONStorage(() => storage),
            }
          )
        )
      : create(() => ({
          value: initialValue,
          set: (value: VALUE): void => hook.setState({ value }),
        }))
  ) as ScalarStore<VALUE>;

  hook.getValue = (): VALUE => hook.getState().value;
  hook.setValue = (value: VALUE): void => hook.setState({ value });

  return hook;
};
